import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import JetstarBspService, { JetstarBspImportSource } from '~/services/JetstarBspService';

const POLLING_INTERVAL_MS = 10000;
const TERMINAL_IMPORT_STATUSES = new Set(['completed', 'completed_with_errors', 'failed']);

export const jetstarBspQueryKeys = {
  importStatus: (importId: string) => ['jetstarBspImportStatus', importId] as const,
  importResults: (importId: string, page: number, pageSize: number) =>
    ['jetstarBspImportResults', importId, page, pageSize] as const,
};

export function useUploadJetstarBspImport() {
  return useMutation({
    mutationFn: ({ file, source }: { file: File; source: JetstarBspImportSource }) =>
      JetstarBspService.uploadImport(file, source),
  });
}

export function useJetstarBspImportStatus(importId: string | null) {
  return useQuery({
    queryKey: jetstarBspQueryKeys.importStatus(importId || ''),
    queryFn: () => JetstarBspService.getImportStatus(importId),
    enabled: Boolean(importId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || TERMINAL_IMPORT_STATUSES.has(status)) {
        return false;
      }

      return POLLING_INTERVAL_MS;
    },
  });
}

export function useJetstarBspImportResults(importId: string | null, page: number, pageSize: number) {
  return useQuery({
    queryKey: jetstarBspQueryKeys.importResults(importId || '', page, pageSize),
    queryFn: () => JetstarBspService.getImportResults(importId, page, pageSize),
    enabled: Boolean(importId),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || TERMINAL_IMPORT_STATUSES.has(status)) {
        return false;
      }

      return POLLING_INTERVAL_MS;
    },
  });
}

export function useDownloadJetstarBspImportErrorsCsv() {
  return useMutation({
    mutationFn: (importId: string) => JetstarBspService.downloadImportErrorsCsv(importId),
  });
}
